import clsx from 'clsx';
import React, { Suspense, lazy } from 'react';

import useCurrentUser from '@hooks/useCurrentUser';
import useEventTracking from '@hooks/useEventTracking';
import useServerSideRendering from '@hooks/useServerSideRendering';
import { ContentType, Page } from '@tracking';

import { AudioProvider } from './AudioContext';
import Data from './Layouts/Data';
import Feature from './Layouts/Feature';
import Graphic from './Layouts/Graphic';
import LockedArticle from './Layouts/LockedArticle';
import Standard from './Layouts/Standard';
import Weekend from './Layouts/Weekend';
import { type ArticleProps, ArticleTheme, type ThemeViewsProps } from './interfaces';

const Paywall = lazy(() => import('./Paywall'));

const OFFER_PARAM = 'offer';
const SHARED_PARAM = 'shared';

const getArticleTemplate = (theme: string) => {
  switch (theme) {
    case ArticleTheme.Feature:
      return Feature;
    case ArticleTheme.Graphic:
      return Graphic;
    case ArticleTheme.Weekend:
      return Weekend;
    case ArticleTheme.Data:
      return Data;
    default:
      return Standard;
  }
};

const ArticleLayout = ({
  article,
  env,
  hideAwarenessBar,
  isWebview,
  midArticleUnitType,
  mostPopularArticles,
  newsletterId,
  relatedArticles,
  relatedEvent,
  showUpsellPaywall,
  theme,
}: ArticleProps) => {
  const isLocked = article.access === 'locked';

  const themeProps: ThemeViewsProps = {
    article,
    env,
    isWebview,
    midArticleUnitType,
    mostPopularArticles,
    newsletterId,
    relatedArticles,
    relatedEvent,
  };

  const ArticleTemplate = getArticleTemplate(theme);
  const hydrated = useServerSideRendering();
  const { currentUser } = useCurrentUser();
  const { Track } = useEventTracking({
    contentId: article.id,
    contentType: ContentType.Article,
    page: Page.Article,
    pageId: article.id,
  });

  // Read URL params only after hydration — window.location is not available during SSR.
  const offerCode = hydrated
    ? new URLSearchParams(window.location.search).get(OFFER_PARAM) ?? undefined
    : undefined;
  const shareGuid = hydrated
    ? new URLSearchParams(window.location.search).get(SHARED_PARAM) ?? undefined
    : undefined;

  // Two independent sticky-paywall cases:
  // 1. showUpsellPaywall — server-computed: visit was via known-leads or email-entry unlock path
  // 2. Pro upsell — client-only: user tier is dynamic; non-pro subscriber on a locked pro article
  const shouldShowStickyPaywall =
    showUpsellPaywall ||
    (isLocked && article.isPro && !!currentUser?.is_subscriber && !currentUser?.is_pro_subscriber);

  return (
    <AudioProvider>
      <Track>
        <article
          className={clsx('relative', {
            'pt-32 sm:pt-48': !isWebview && !isLocked && !showUpsellPaywall,
            'pt-90 sm:pt-[140px]':
              !isWebview &&
              (isLocked || showUpsellPaywall) &&
              (shouldShowStickyPaywall || hideAwarenessBar),
            'pt-[155px] sm:pt-[140px]':
              !isWebview && isLocked && !shouldShowStickyPaywall && !hideAwarenessBar,
          })}
        >
          {isLocked && !isWebview && !shouldShowStickyPaywall ? (
            <LockedArticle offerCode={offerCode} shareGuid={shareGuid} {...themeProps} />
          ) : (
            <ArticleTemplate {...themeProps} />
          )}
        </article>
        {/* Display this paywall only when:
            - The article is free and targeted at known leads, or
            - The user is a subscriber with an annual plan but requires a Pro subscription */}
        {hydrated && shouldShowStickyPaywall && (
          <Suspense>
            <div
              className={clsx('sticky -bottom-px z-50 mt-0 w-full bg-grayscale-charcoal')}
              data-testid="paywall-container"
            >
              <Paywall
                articleId={article.id}
                isTwoOptPaywall
                offerCode={offerCode}
                shareGuid={shareGuid}
              />
            </div>
          </Suspense>
        )}
      </Track>
    </AudioProvider>
  );
};

export default ArticleLayout;
